import { Injectable } from '@angular/core';
import {
  API_SERVICES_CONFIG,
  ApiResponseWithQueryContext,
  DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  HttpGETCustomOptions,
  NonPaginatedResourceConfig,
  PaginatedResourceConfig,
  PaginationService,
  PortalHttpClient,
  QueryParams,
} from '@grid-ui/common';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CountryRiskProfile,
  CountryRiskProfileCollection,
  CountryRiskProfileDetail,
  CountryRiskProfilesParameters,
  CountryRiskProfileSubscriptionParameters,
  CountryRiskProfileSubscriptionsCollection,
} from '../../../shared-models';
import {
  ApiCountryRiskProfile,
  ApiCountryRiskProfileDetails,
  ApiCountryRiskProfilesResult,
  ApiCountryRiskProfilesSubscription,
} from '../models';

@Injectable()
export class CountryRiskProfilesService {
  private profilesResourceConfig: PaginatedResourceConfig;
  private profileSnapshotResourceConfig: NonPaginatedResourceConfig;
  private profileDetailNessieResourceConfig: NonPaginatedResourceConfig;
  private profilesSearchResourceConfig: PaginatedResourceConfig;
  private profileSubscriptionNessieResourceConfig: NonPaginatedResourceConfig;

  constructor(
    private readonly http: PortalHttpClient,
    private readonly paginationService: PaginationService,
  ) {
    this.profilesResourceConfig = API_SERVICES_CONFIG.nessie.profiles._configuration;
    this.profileSnapshotResourceConfig = API_SERVICES_CONFIG.nessie.profiles.detail._configuration;
    this.profileDetailNessieResourceConfig = API_SERVICES_CONFIG.nessie.profiles.detail.individual._configuration;
    this.profilesSearchResourceConfig = API_SERVICES_CONFIG.nessie.profiles.search._configuration;
    this.profileSubscriptionNessieResourceConfig = API_SERVICES_CONFIG.nessie.profiles.subscription._configuration;
  }

  /**
   * Get a collection of paginated Profile listing items based on the specified query parameters.
   *
   * @param parameters A query parameter object
   * @param options An optional argument with custom options for the underlying Http GET request
   */
  public getProfiles(
    profilesParams: CountryRiskProfilesParameters | null,
    options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  ): Observable<CountryRiskProfileCollection> {
    const queryParams: QueryParams = profilesParams || {};
    return this.http
      .getPaginated<ApiCountryRiskProfilesResult>(this.profilesResourceConfig, {
        ...options,
        queryParams,
      })
      .pipe(
        map((apiResponseContext: ApiResponseWithQueryContext<ApiCountryRiskProfilesResult>) => ({
          total: apiResponseContext.response.total,
          results: apiResponseContext.response.results,
          paginationContext: this.paginationService.getNewPaginationContext(
            apiResponseContext.response.links,
            apiResponseContext.queryParams,
          ),
        })),
      );
  }

  // /**
  //    * Get a collection of paginated Profile listing items based on the specified query parameters.
  //    *
  //    * @param parameters A query parameter object
  //    * @param options An optional argument with custom options for the underlying Http GET request
  //    */

  // public getAllProfiles(
  //   profilesParams: CountryRiskProfilesParameters = { page : 1, page_size: 250},
  //   options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  // ): Observable<CountryRiskProfileCollection> {
  //   // const queryParams: QueryParams = profilesParams || {};
  //   const queryParams = [ profilesParams, options] as const;
  //   return this.getProfiles(...queryParams).pipe(
  //     expand(
  //     data => data?.paginationContext?.next
  //     ? this.getProfiles(...queryParams)
  //     : EMPTY
  //     ),
  //     reduce((acc, data) => ({
  //       ...data,
  //       results: acc.results.concat(data.results),
  //     })),
  //   );
  // }

  // public getProfilesAll(
  //   profilesParams: CountryRiskProfilesParameters | null,
  //   options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS
  // ): Observable<CountryRiskProfileCollection> {
  //   const queryParams: QueryParams = profilesParams || {};
  //   return this.http.getPaginated<ApiCountryRiskProfilesResult>(
  //     this.profilesResourceConfig,
  //     {
  //       ...options,
  //       queryParams
  //     }
  //   ).pipe(
  //     map((apiResponseContext: ApiResponseWithQueryContext<ApiCountryRiskProfilesResult>) => ({
  //       total: apiResponseContext.response.total,
  //       results: apiResponseContext.response.results,
  //       paginationContext: this.paginationService.getNewPaginationContext(
  //         apiResponseContext.response.links,
  //         apiResponseContext.queryParams
  //       ),
  //     }))
  //   );
  // }

  public getProfileSearch(
    profilesParams: any | null,
    options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  ): Observable<CountryRiskProfileCollection> {
    const queryParams: QueryParams = profilesParams || {};
    return this.http
      .getPaginated<ApiCountryRiskProfilesResult>(this.profilesSearchResourceConfig, {
        ...options,
        queryParams,
      })
      .pipe(
        map((apiResponseContext: ApiResponseWithQueryContext<ApiCountryRiskProfilesResult>) => ({
          total: apiResponseContext.response.total,
          results: apiResponseContext.response.results,
          paginationContext: this.paginationService.getNewPaginationContext(
            apiResponseContext.response.links,
            apiResponseContext.queryParams,
          ),
        })),
      );
  }

  /**
   * Get the specified country risk profile detail.
   *
   * @param id Id of country risk profile detail to retrieve.
   * @param options An optional argument with custom options for the underlying Http GET request
   */
  public getProfileSnapshot(
    id: string,
    options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  ): Observable<CountryRiskProfileDetail> {
    return this.http.get<ApiCountryRiskProfileDetails>(this.profileSnapshotResourceConfig, {
      ...options,
      pathParams: { id },
    });
  }

  /**
   * Get the specified country risk profile detail.
   *
   * @param id Id of country risk profile detail to retrieve.
   * @param options An optional argument with custom options for the underlying Http GET request
   */

  public getProfileDetail(
    id: string | number,
    options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  ): Observable<CountryRiskProfile> {
    return this.http.get<ApiCountryRiskProfile>(this.profileDetailNessieResourceConfig, {
      ...options,
      pathParams: { id },
    });
  }

  /**
   * Get the URL path segments for Profile Details
   */
  public getProfileDetailUrlPath(): string {
    return this.profileSnapshotResourceConfig.path;
  }

  /**
   * Get a collection of paginated Profile listing items based on the specified query parameters.
   *
   * Note: The returned collection contains both restricted and unrestricted profile items.
   *
   * @param parameters A query parameter object
   * @param options An optional argument with custom options for the underlying Http GET request
   */
  public getProfileSubscriptions(
    parameters: CountryRiskProfileSubscriptionParameters | null,
    options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  ): Observable<CountryRiskProfileSubscriptionsCollection> {
    const queryParams: QueryParams = { ...parameters };
    return this.http
      .get<ApiCountryRiskProfilesSubscription>(this.profileSubscriptionNessieResourceConfig, {
        ...options,
        queryParams,
      })
      .pipe(
        map((profileSubscriptionsResult: ApiCountryRiskProfilesSubscription) => ({
          total: profileSubscriptionsResult.total,
          results: profileSubscriptionsResult.results,
          paginationContext: this.paginationService.getNewPaginationContext(profileSubscriptionsResult.links, parameters),
        })),
      );
  }
}
